import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft, 
  Save, 
  Eye,
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronRight,
  Image as ImageIcon,
  Video,
  Type,
  Loader2,
  ExternalLink,
  Settings,
  Palette,
  Layout
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper for authenticated fetch
const authFetch = (url, options = {}) => {
  const token = localStorage.getItem('session_token');
  const headers = { ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return fetch(url, { ...options, credentials: 'include', headers });
};

// Section type configurations
const SECTION_TYPES = {
  hero: { icon: Layout, label: 'Hero', description: 'Full-width hero with background' },
  about: { icon: Type, label: 'About', description: 'Bio and profile image' },
  projects: { icon: Layout, label: 'Projects', description: 'Grid of work/projects' },
  press: { icon: Type, label: 'Press', description: 'Press mentions and articles' },
  gallery: { icon: ImageIcon, label: 'Gallery', description: 'Swipeable image carousel' },
  fullwidth_image: { icon: ImageIcon, label: 'Full-Width Image', description: 'Image that spans full screen width' },
  video: { icon: Video, label: 'Video Reel', description: 'Embedded video section' },
  book: { icon: Type, label: 'Book/Product', description: 'Featured product with CTA' },
  contact: { icon: Type, label: 'Contact', description: 'Contact information or form' },
  custom: { icon: Type, label: 'Custom', description: 'Custom section with your own title' }
};

// Font packages
const FONT_PACKAGES = {
  editorial: { name: 'Editorial', preview: 'Playfair Display + Source Sans' },
  modern: { name: 'Modern', preview: 'Inter' },
  bold: { name: 'Bold', preview: 'Oswald + Roboto' },
  elegant: { name: 'Elegant', preview: 'Cormorant Garamond + Lato' },
  minimal: { name: 'Minimal', preview: 'Helvetica Neue' }
};

// Section Editor Component
const SectionEditor = ({ section, onUpdate, onDelete, artists = [], projects = [], companies = [], index, onDragStart, onDragOver, onDrop, isDragging }) => {
  const [expanded, setExpanded] = useState(false);
  const sectionType = SECTION_TYPES[section.type] || SECTION_TYPES.custom;
  const Icon = sectionType.icon;

  // Toggle selection of an entity
  const toggleEntity = (type, entity) => {
    const fieldName = `linked_${type}`;
    const currentList = section[fieldName] || [];
    // Handle irregular plural: companies -> company_id
    const singularType = type === 'companies' ? 'company' : type.slice(0, -1);
    const entityId = entity[`${singularType}_id`] || entity._id;
    
    if (currentList.some(e => (e[`${singularType}_id`] || e._id) === entityId)) {
      onUpdate({ 
        ...section, 
        [fieldName]: currentList.filter(e => (e[`${singularType}_id`] || e._id) !== entityId)
      });
    } else {
      onUpdate({ 
        ...section, 
        [fieldName]: [...currentList, entity]
      });
    }
  };

  const isEntitySelected = (type, entity) => {
    const fieldName = `linked_${type}`;
    const currentList = section[fieldName] || [];
    // Handle irregular plural: companies -> company_id
    const singularType = type === 'companies' ? 'company' : type.slice(0, -1);
    const entityId = entity[`${singularType}_id`] || entity._id;
    return currentList.some(e => (e[`${singularType}_id`] || e._id) === entityId);
  };

  return (
    <Card 
      className={`transition-all ${!section.enabled ? 'opacity-50' : ''} ${isDragging ? 'opacity-50 border-dashed border-2 border-primary' : ''}`}
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDrop={(e) => onDrop(e, index)}
    >
      <div 
        className="p-4 flex items-center gap-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <GripVertical 
          className="text-muted-foreground cursor-grab hover:text-primary transition-colors" 
          size={18} 
          onMouseDown={(e) => e.stopPropagation()}
        />
        <Icon size={18} className="text-muted-foreground" />
        <div className="flex-1">
          <span className="font-medium">{section.title || sectionType.label}</span>
          <span className="text-xs text-muted-foreground ml-2">({section.type})</span>
        </div>
        <label className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={section.enabled}
            onChange={(e) => onUpdate({ ...section, enabled: e.target.checked })}
            className="rounded"
          />
          <span className="text-sm">Enabled</span>
        </label>
        {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-border pt-4 space-y-4">
          {/* Section Title */}
          <div>
            <label className="block text-sm font-medium mb-1">Section Title</label>
            <input
              type="text"
              value={section.title || ''}
              onChange={(e) => onUpdate({ ...section, title: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded"
              placeholder={sectionType.label}
            />
          </div>

          {/* Subtitle */}
          <div>
            <label className="block text-sm font-medium mb-1">Subtitle</label>
            <input
              type="text"
              value={section.subtitle || ''}
              onChange={(e) => onUpdate({ ...section, subtitle: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded"
              placeholder="Optional subtitle"
            />
          </div>

          {/* Hero Logo - replaces title with image */}
          {section.type === 'hero' && (
            <div>
              <label className="block text-sm font-medium mb-1">Hero Logo (Optional)</label>
              <input
                type="text"
                value={section.hero_logo || ''}
                onChange={(e) => onUpdate({ ...section, hero_logo: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded"
                placeholder="Logo image URL (.png recommended)"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Add a logo/title image to display instead of text. Leave empty to use the site name as text.
              </p>
            </div>
          )}

          {/* Hero Content Position */}
          {section.type === 'hero' && (
            <div>
              <label className="block text-sm font-medium mb-1">Content Position</label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { value: 'center', label: 'Center', icon: '⊕' },
                  { value: 'top-center', label: 'Top', icon: '⬆' },
                  { value: 'bottom-center', label: 'Bottom', icon: '⬇' },
                  { value: 'bottom-right', label: 'Bottom Right', icon: '⬊' }
                ].map(pos => (
                  <button
                    key={pos.value}
                    type="button"
                    onClick={() => onUpdate({ ...section, content_position: pos.value })}
                    className={`p-2 rounded text-sm flex flex-col items-center gap-1 transition-colors ${
                      (section.content_position || 'center') === pos.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted/50 hover:bg-muted'
                    }`}
                  >
                    <span className="text-lg">{pos.icon}</span>
                    <span className="text-xs">{pos.label}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Choose where to position the logo/title to avoid obstructing video content.
              </p>
            </div>
          )}

          {/* Content - for text-heavy sections */}
          {['about', 'custom', 'book', 'contact'].includes(section.type) && (
            <div>
              <label className="block text-sm font-medium mb-1">Content</label>
              <textarea
                value={section.content || ''}
                onChange={(e) => onUpdate({ ...section, content: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded"
                rows={4}
                placeholder="Section content..."
              />
            </div>
          )}

          {/* Image */}
          <div>
            <label className="block text-sm font-medium mb-1">Image URL</label>
            <input
              type="text"
              value={section.image || ''}
              onChange={(e) => onUpdate({ ...section, image: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-border rounded"
              placeholder="https://... or Google Drive URL"
            />
          </div>

          {/* Projects Section Editor */}
          {section.type === 'projects' && (
            <div className="space-y-4">
              {/* Select CR Projects */}
              {projects.length > 0 && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium">Select Projects from Cultural Revolution</label>
                  <div className="flex flex-wrap gap-2">
                    {projects.map(project => (
                      <button
                        key={project.project_id}
                        type="button"
                        onClick={() => toggleEntity('projects', project)}
                        className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                          isEntitySelected('projects', project)
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted hover:bg-muted/80'
                        }`}
                      >
                        {project.title}
                      </button>
                    ))}
                  </div>
                  {(section.linked_projects?.length > 0) && (
                    <p className="text-xs text-green-500">{section.linked_projects.length} CR project(s) selected - these will display automatically</p>
                  )}
                </div>
              )}

              {/* Custom Project Items with Video & Description */}
              <div className="border-t border-border pt-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium">Custom Project Items</label>
                  <button
                    type="button"
                    onClick={() => onUpdate({
                      ...section,
                      items: [...(section.items || []), { 
                        id: `proj_${Date.now()}`,
                        title: 'New Project',
                        description: '',
                        image: '',
                        video_url: '',
                        link: ''
                      }]
                    })}
                    className="text-xs px-2 py-1 bg-primary text-primary-foreground rounded"
                  >
                    + Add Custom Project
                  </button>
                </div>
                
                {(section.items || []).map((item, idx) => (
                  <div key={item.id || idx} className="p-4 bg-muted/30 rounded space-y-3 mb-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Custom Project {idx + 1}</span>
                      <button
                        type="button"
                        onClick={() => onUpdate({
                          ...section,
                          items: section.items.filter((_, i) => i !== idx)
                        })}
                        className="text-red-500 text-xs hover:text-red-400"
                      >
                        Remove
                      </button>
                    </div>
                    <input
                      type="text"
                      value={item.title || ''}
                      onChange={(e) => {
                        const newItems = [...section.items];
                        newItems[idx] = { ...item, title: e.target.value };
                        onUpdate({ ...section, items: newItems });
                      }}
                      className="w-full px-2 py-1 bg-background border border-border rounded text-sm"
                      placeholder="Project Title"
                    />
                    <textarea
                      value={item.description || item.dek || ''}
                      onChange={(e) => {
                        const newItems = [...section.items];
                        newItems[idx] = { ...item, description: e.target.value, dek: e.target.value };
                        onUpdate({ ...section, items: newItems });
                      }}
                      className="w-full px-2 py-1 bg-background border border-border rounded text-sm"
                      rows={2}
                      placeholder="Project description..."
                    />
                    <input
                      type="text"
                      value={item.image || ''}
                      onChange={(e) => {
                        const newItems = [...section.items];
                        newItems[idx] = { ...item, image: e.target.value };
                        onUpdate({ ...section, items: newItems });
                      }}
                      className="w-full px-2 py-1 bg-background border border-border rounded text-sm"
                      placeholder="Image URL (Google Drive supported)"
                    />
                    <input
                      type="text"
                      value={item.video_url || ''}
                      onChange={(e) => {
                        const newItems = [...section.items];
                        newItems[idx] = { ...item, video_url: e.target.value };
                        onUpdate({ ...section, items: newItems });
                      }}
                      className="w-full px-2 py-1 bg-background border border-border rounded text-sm"
                      placeholder="Video URL (YouTube or .mp4)"
                    />
                    <input
                      type="text"
                      value={item.link || item.external_url || ''}
                      onChange={(e) => {
                        const newItems = [...section.items];
                        newItems[idx] = { ...item, link: e.target.value, external_url: e.target.value };
                        onUpdate({ ...section, items: newItems });
                      }}
                      className="w-full px-2 py-1 bg-background border border-border rounded text-sm"
                      placeholder="External Link URL"
                    />
                  </div>
                ))}
                
                {(!section.items || section.items.length === 0) && (!section.linked_projects || section.linked_projects.length === 0) && (
                  <p className="text-xs text-muted-foreground">Select CR projects above or add custom projects.</p>
                )}
              </div>
            </div>
          )}

          {/* Link Existing CR Entities - Artists for Projects/Companies sections */}
          {['projects', 'companies'].includes(section.type) && artists.length > 0 && (
            <div className="space-y-2">
              <label className="block text-sm font-medium">Link Artists from Cultural Revolution</label>
              <div className="flex flex-wrap gap-2">
                {artists.map(artist => (
                  <button
                    key={artist.artist_id}
                    type="button"
                    onClick={() => toggleEntity('artists', artist)}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                      isEntitySelected('artists', artist)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted hover:bg-muted/80'
                    }`}
                  >
                    {artist.name}
                  </button>
                ))}
              </div>
              {(section.linked_artists?.length > 0) && (
                <p className="text-xs text-green-500">{section.linked_artists.length} artist(s) selected</p>
              )}
            </div>
          )}

          {/* Link Existing CR Entities - Projects for Artists/Companies sections */}
          {['about', 'custom', 'companies'].includes(section.type) && projects.length > 0 && (
            <div className="space-y-2">
              <label className="block text-sm font-medium">Link Projects from Cultural Revolution</label>
              <div className="flex flex-wrap gap-2">
                {projects.map(project => (
                  <button
                    key={project.project_id}
                    type="button"
                    onClick={() => toggleEntity('projects', project)}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                      isEntitySelected('projects', project)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted hover:bg-muted/80'
                    }`}
                  >
                    {project.title}
                  </button>
                ))}
              </div>
              {(section.linked_projects?.length > 0) && (
                <p className="text-xs text-green-500">{section.linked_projects.length} project(s) selected</p>
              )}
            </div>
          )}

          {/* Link Existing CR Entities - Companies for Projects/Artists sections */}
          {['projects', 'about', 'custom'].includes(section.type) && companies.length > 0 && (
            <div className="space-y-2">
              <label className="block text-sm font-medium">Link Companies from Cultural Revolution</label>
              <div className="flex flex-wrap gap-2">
                {companies.map(company => (
                  <button
                    key={company.company_id}
                    type="button"
                    onClick={() => toggleEntity('companies', company)}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                      isEntitySelected('companies', company)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted hover:bg-muted/80'
                    }`}
                  >
                    {company.name}
                  </button>
                ))}
              </div>
              {(section.linked_companies?.length > 0) && (
                <p className="text-xs text-green-500">{section.linked_companies.length} company(ies) selected</p>
              )}
            </div>
          )}

          {/* Press Section Editor - Filters & Custom Press Items */}
          {section.type === 'press' && (
            <div className="space-y-4">
              {/* Filter Press by CR Entities */}
              <div className="p-4 bg-muted/20 rounded-lg space-y-4">
                <p className="text-sm font-medium">Filter Press by Cultural Revolution Entities</p>
                <p className="text-xs text-muted-foreground">Press will auto-populate from content related to selected entities.</p>
                
                {/* Filter by Artists */}
                {artists.length > 0 && (
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-muted-foreground">By Artists:</label>
                    <div className="flex flex-wrap gap-2">
                      {artists.map(artist => (
                        <button
                          key={artist.artist_id}
                          type="button"
                          onClick={() => toggleEntity('artists', artist)}
                          className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                            isEntitySelected('artists', artist)
                              ? 'bg-blue-500 text-white'
                              : 'bg-muted hover:bg-muted/80'
                          }`}
                        >
                          {artist.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Filter by Projects */}
                {projects.length > 0 && (
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-muted-foreground">By Projects:</label>
                    <div className="flex flex-wrap gap-2">
                      {projects.map(project => (
                        <button
                          key={project.project_id}
                          type="button"
                          onClick={() => toggleEntity('projects', project)}
                          className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                            isEntitySelected('projects', project)
                              ? 'bg-green-500 text-white'
                              : 'bg-muted hover:bg-muted/80'
                          }`}
                        >
                          {project.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Filter by Companies */}
                {companies.length > 0 && (
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-muted-foreground">By Companies:</label>
                    <div className="flex flex-wrap gap-2">
                      {companies.map(company => (
                        <button
                          key={company.company_id}
                          type="button"
                          onClick={() => toggleEntity('companies', company)}
                          className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                            isEntitySelected('companies', company)
                              ? 'bg-purple-500 text-white'
                              : 'bg-muted hover:bg-muted/80'
                          }`}
                        >
                          {company.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Summary of filters */}
                <div className="text-xs text-muted-foreground pt-2 border-t border-border">
                  Filtering: {' '}
                  {section.linked_artists?.length > 0 && <span className="text-blue-400">{section.linked_artists.length} artists</span>}
                  {section.linked_artists?.length > 0 && (section.linked_projects?.length > 0 || section.linked_companies?.length > 0) && ', '}
                  {section.linked_projects?.length > 0 && <span className="text-green-400">{section.linked_projects.length} projects</span>}
                  {section.linked_projects?.length > 0 && section.linked_companies?.length > 0 && ', '}
                  {section.linked_companies?.length > 0 && <span className="text-purple-400">{section.linked_companies.length} companies</span>}
                  {!section.linked_artists?.length && !section.linked_projects?.length && !section.linked_companies?.length && 'All press content'}
                </div>
              </div>

              {/* Custom Press Items */}
              <div className="border-t border-border pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <label className="block text-sm font-medium">Submit Press Items</label>
                    <p className="text-xs text-muted-foreground">Add press items that will be sent to Cultural Revolution for approval.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onUpdate({
                      ...section,
                      press_items: [...(section.press_items || []), { 
                        id: `press_${Date.now()}`,
                        title: '',
                        source: '',
                        url: '',
                        date: new Date().toISOString().split('T')[0],
                        status: 'pending'
                      }]
                    })}
                    className="text-xs px-2 py-1 bg-primary text-primary-foreground rounded"
                  >
                    + Add Press Item
                  </button>
                </div>
                
                {(section.press_items || []).map((item, idx) => (
                  <div key={item.id || idx} className="p-4 bg-muted/30 rounded space-y-2 mb-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Press Item {idx + 1}</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          item.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                          item.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {item.status || 'pending'}
                        </span>
                        <button
                          type="button"
                          onClick={() => onUpdate({
                            ...section,
                            press_items: section.press_items.filter((_, i) => i !== idx)
                          })}
                          className="text-red-500 text-xs hover:text-red-400"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    <input
                      type="text"
                      value={item.title || ''}
                      onChange={(e) => {
                        const newItems = [...section.press_items];
                        newItems[idx] = { ...item, title: e.target.value };
                        onUpdate({ ...section, press_items: newItems });
                      }}
                      className="w-full px-2 py-1 bg-background border border-border rounded text-sm"
                      placeholder="Article Title"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={item.source || ''}
                        onChange={(e) => {
                          const newItems = [...section.press_items];
                          newItems[idx] = { ...item, source: e.target.value };
                          onUpdate({ ...section, press_items: newItems });
                        }}
                        className="w-full px-2 py-1 bg-background border border-border rounded text-sm"
                        placeholder="Source (e.g., Variety)"
                      />
                      <input
                        type="date"
                        value={item.date || ''}
                        onChange={(e) => {
                          const newItems = [...section.press_items];
                          newItems[idx] = { ...item, date: e.target.value };
                          onUpdate({ ...section, press_items: newItems });
                        }}
                        className="w-full px-2 py-1 bg-background border border-border rounded text-sm"
                      />
                    </div>
                    <input
                      type="text"
                      value={item.url || ''}
                      onChange={(e) => {
                        const newItems = [...section.press_items];
                        newItems[idx] = { ...item, url: e.target.value };
                        onUpdate({ ...section, press_items: newItems });
                      }}
                      className="w-full px-2 py-1 bg-background border border-border rounded text-sm"
                      placeholder="Article URL"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Full-Width Image Section Editor */}
          {section.type === 'fullwidth_image' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Full-Width Image URL</label>
                <input
                  type="text"
                  value={section.fullwidth_image || ''}
                  onChange={(e) => onUpdate({ ...section, fullwidth_image: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded"
                  placeholder="Image URL or Google Drive link"
                />
                <p className="text-xs text-muted-foreground mt-1">Image will fill the screen width and maintain proportions</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Caption (Optional)</label>
                <input
                  type="text"
                  value={section.caption || ''}
                  onChange={(e) => onUpdate({ ...section, caption: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded"
                  placeholder="Photo credit or caption"
                />
              </div>
            </div>
          )}

          {/* Gallery Section Editor */}
          {section.type === 'gallery' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium">Gallery Items</label>
                  <p className="text-xs text-muted-foreground">Add images or YouTube videos for a swipeable carousel</p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => onUpdate({
                      ...section,
                      gallery_images: [...(section.gallery_images || []), { 
                        id: `img_${Date.now()}`,
                        type: 'image',
                        url: '',
                        caption: ''
                      }]
                    })}
                    className="text-xs px-2 py-1 bg-primary text-primary-foreground rounded"
                  >
                    + Add Image
                  </button>
                  <button
                    type="button"
                    onClick={() => onUpdate({
                      ...section,
                      gallery_images: [...(section.gallery_images || []), { 
                        id: `vid_${Date.now()}`,
                        type: 'video',
                        url: '',
                        caption: ''
                      }]
                    })}
                    className="text-xs px-2 py-1 bg-red-600 text-white rounded"
                  >
                    + Add Video
                  </button>
                </div>
              </div>
              
              {(section.gallery_images || []).map((item, idx) => (
                <div key={item.id || idx} className={`p-3 rounded space-y-2 ${item.type === 'video' ? 'bg-red-500/10 border border-red-500/20' : 'bg-muted/30'}`}>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium flex items-center gap-2">
                      {item.type === 'video' ? '🎬 Video' : '🖼️ Image'} {idx + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => onUpdate({
                        ...section,
                        gallery_images: section.gallery_images.filter((_, i) => i !== idx)
                      })}
                      className="text-red-500 text-xs hover:text-red-400"
                    >
                      Remove
                    </button>
                  </div>
                  <input
                    type="text"
                    value={item.url || ''}
                    onChange={(e) => {
                      const newImages = [...section.gallery_images];
                      newImages[idx] = { ...item, url: e.target.value };
                      onUpdate({ ...section, gallery_images: newImages });
                    }}
                    className="w-full px-2 py-1 bg-background border border-border rounded text-sm"
                    placeholder={item.type === 'video' ? 'YouTube URL (e.g., https://youtube.com/watch?v=...)' : 'Image URL (Google Drive supported)'}
                  />
                  <input
                    type="text"
                    value={item.caption || ''}
                    onChange={(e) => {
                      const newImages = [...section.gallery_images];
                      newImages[idx] = { ...item, caption: e.target.value };
                      onUpdate({ ...section, gallery_images: newImages });
                    }}
                    className="w-full px-2 py-1 bg-background border border-border rounded text-sm"
                    placeholder="Caption (optional)"
                  />
                  {item.url && item.type !== 'video' && (
                    <div className="mt-2 rounded overflow-hidden bg-black/20 h-20">
                      <img 
                        src={item.url.includes('drive.google.com') 
                          ? `https://drive.google.com/thumbnail?id=${item.url.match(/\/d\/([a-zA-Z0-9_-]+)/)?.[1] || item.url.match(/id=([a-zA-Z0-9_-]+)/)?.[1]}&sz=w400`
                          : item.url
                        } 
                        alt={`Preview ${idx + 1}`}
                        className="h-full w-auto object-contain mx-auto"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                  {item.url && item.type === 'video' && (
                    <div className="mt-2 rounded overflow-hidden bg-black/20 h-20 flex items-center justify-center">
                      {(() => {
                        const match = item.url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
                        const videoId = match ? match[1] : null;
                        return videoId ? (
                          <img 
                            src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                            alt={`Video Preview ${idx + 1}`}
                            className="h-full w-auto object-contain"
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">Invalid YouTube URL</span>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ))}
              
              {(!section.gallery_images || section.gallery_images.length === 0) && (
                <p className="text-xs text-muted-foreground p-4 text-center bg-muted/20 rounded">
                  No items added. Click "+ Add Image" or "+ Add Video" to create your gallery.
                </p>
              )}
              
              <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded text-xs text-blue-400">
                <strong>💡 Tips:</strong>
                <ul className="mt-1 space-y-1 list-disc list-inside">
                  <li>For Google Drive images, use the share link</li>
                  <li>For YouTube videos, paste the video URL (youtube.com/watch?v=...)</li>
                  <li>Videos will show with a play button overlay</li>
                </ul>
              </div>
            </div>
          )}

          {/* Video URL - for video and hero sections */}
          {['video', 'hero'].includes(section.type) && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Video URL - Desktop</label>
                <input
                  type="text"
                  value={section.video_url || ''}
                  onChange={(e) => onUpdate({ ...section, video_url: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded"
                  placeholder="YouTube URL or direct .mp4 link"
                />
                <p className="text-xs text-muted-foreground mt-1">Supports YouTube, Vimeo, or direct .mp4/.webm files</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Video URL - Mobile (Optional)</label>
                <input
                  type="text"
                  value={section.video_url_mobile || ''}
                  onChange={(e) => onUpdate({ ...section, video_url_mobile: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded"
                  placeholder="Vertical .mp4 link or YouTube URL"
                />
                <p className="text-xs text-muted-foreground mt-1">Portrait/vertical video for mobile. Leave empty to use desktop video.</p>
              </div>
              
              {/* Hide Video Controls Option */}
              <div className="flex items-center gap-3 p-3 bg-muted/30 rounded">
                <input
                  type="checkbox"
                  id={`hide-video-title-${section.section_id}`}
                  checked={section.hide_video_title || false}
                  onChange={(e) => onUpdate({ ...section, hide_video_title: e.target.checked })}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor={`hide-video-title-${section.section_id}`} className="text-sm cursor-pointer">
                  <span className="font-medium">Hide YouTube title & branding</span>
                  <p className="text-xs text-muted-foreground">Covers the top area where YouTube shows the video title</p>
                </label>
              </div>
              
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded text-xs text-green-400">
                <strong>✓ iOS Tip:</strong> Use direct .mp4 files for reliable autoplay on iPhones. YouTube embeds may require user interaction to play on iOS.
              </div>
            </div>
          )}

          {/* Background Settings */}
          <div className="p-3 bg-muted/30 rounded space-y-3">
            <p className="text-sm font-medium">Background</p>
            <div className="grid grid-cols-3 gap-2">
              {['color', 'image', 'video'].map(type => (
                <button
                  key={type}
                  onClick={() => onUpdate({ ...section, background_type: type })}
                  className={`px-3 py-2 rounded text-sm capitalize ${
                    section.background_type === type 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-background border border-border'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
            
            {section.background_type === 'color' && (
              <div className="flex gap-2">
                <input
                  type="color"
                  value={section.background_value || '#0a0a0a'}
                  onChange={(e) => onUpdate({ ...section, background_value: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={section.background_value || ''}
                  onChange={(e) => onUpdate({ ...section, background_value: e.target.value })}
                  className="flex-1 px-3 py-2 bg-background border border-border rounded"
                  placeholder="#0a0a0a"
                />
              </div>
            )}
            
            {section.background_type === 'image' && (
              <input
                type="text"
                value={section.background_value || ''}
                onChange={(e) => onUpdate({ ...section, background_value: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded"
                placeholder="Background image URL"
              />
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Overlay</label>
                <select
                  value={section.overlay_color || 'black'}
                  onChange={(e) => onUpdate({ ...section, overlay_color: e.target.value })}
                  className="w-full px-2 py-1 bg-background border border-border rounded text-sm"
                >
                  <option value="black">Black</option>
                  <option value="white">White</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">
                  Opacity: {Math.round((section.overlay_opacity || 0.5) * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={(section.overlay_opacity || 0.5) * 100}
                  onChange={(e) => onUpdate({ ...section, overlay_opacity: parseInt(e.target.value) / 100 })}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* Button/CTA */}
          {['hero', 'book', 'contact', 'custom'].includes(section.type) && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Button Text</label>
                <input
                  type="text"
                  value={section.button_text || ''}
                  onChange={(e) => onUpdate({ ...section, button_text: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded"
                  placeholder="Learn More"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Button URL</label>
                <input
                  type="text"
                  value={section.button_url || ''}
                  onChange={(e) => onUpdate({ ...section, button_url: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded"
                  placeholder="https://..."
                />
              </div>
            </div>
          )}

          {/* Delete Section */}
          <div className="pt-3 border-t border-border">
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-red-500 hover:text-red-600"
              onClick={() => onDelete(section.section_id)}
            >
              <Trash2 size={14} className="mr-1" />
              Delete Section
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export const BrandSiteEditorPage = () => {
  const navigate = useNavigate();
  const { siteId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('sections');
  const [site, setSite] = useState(null);
  
  // CR Entities for linking
  const [crArtists, setCrArtists] = useState([]);
  const [crProjects, setCrProjects] = useState([]);
  const [crCompanies, setCrCompanies] = useState([]);

  useEffect(() => {
    const fetchSite = async () => {
      try {
        const authRes = await authFetch(`${API_URL}/api/auth/me`);
        if (!authRes.ok || (await authRes.json()).role !== 'admin') {
          navigate('/cms');
          return;
        }

        const siteRes = await authFetch(`${API_URL}/api/brand-sites/id/${siteId}`);
        if (siteRes.ok) {
          setSite(await siteRes.json());
        } else {
          navigate('/cms/brand-sites');
        }
        
        // Fetch CR entities for linking
        const [artistsRes, projectsRes, companiesRes] = await Promise.all([
          fetch(`${API_URL}/api/artists`).catch(() => null),
          fetch(`${API_URL}/api/projects`).catch(() => null),
          fetch(`${API_URL}/api/companies`).catch(() => null)
        ]);
        
        if (artistsRes?.ok) setCrArtists(await artistsRes.json());
        if (projectsRes?.ok) setCrProjects(await projectsRes.json());
        if (companiesRes?.ok) {
          const companies = await companiesRes.json();
          setCrCompanies(companies.items || companies || []);
        }
      } catch (error) {
        console.error('Error:', error);
        navigate('/cms/brand-sites');
      } finally {
        setLoading(false);
      }
    };

    fetchSite();
  }, [siteId, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API_URL}/api/brand-sites/${siteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(site)
      });

      if (response.ok) {
        const updated = await response.json();
        setSite(updated);
        alert('Site saved successfully!');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save');
      }
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving site');
    } finally {
      setSaving(false);
    }
  };

  const handleAddSection = (type) => {
    const newSection = {
      section_id: `sec_${Date.now()}`,
      type,
      title: SECTION_TYPES[type]?.label || 'Custom Section',
      enabled: true,
      order: site.sections?.length || 0,
      background_type: 'color',
      background_value: '#0a0a0a',
      overlay_color: 'black',
      overlay_opacity: 0.5
    };

    setSite({
      ...site,
      sections: [...(site.sections || []), newSection]
    });
  };

  const handleUpdateSection = (updatedSection) => {
    setSite({
      ...site,
      sections: site.sections.map(s => 
        s.section_id === updatedSection.section_id ? updatedSection : s
      )
    });
  };

  const handleDeleteSection = (sectionId) => {
    if (!window.confirm('Delete this section?')) return;
    setSite({
      ...site,
      sections: site.sections.filter(s => s.section_id !== sectionId)
    });
  };

  // Drag and drop state and handlers
  const [draggedIndex, setDraggedIndex] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }
    
    const newSections = [...site.sections];
    const [draggedSection] = newSections.splice(draggedIndex, 1);
    newSections.splice(dropIndex, 0, draggedSection);
    
    // Update order values
    const updatedSections = newSections.map((section, idx) => ({
      ...section,
      order: idx
    }));
    
    setSite({ ...site, sections: updatedSections });
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  if (loading || !site) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="editorial-container py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/cms/brand-sites" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft size={20} />
            </Link>
            <div>
              <h1 className="text-lg font-medium">{site.name}</h1>
              <p className="text-xs text-muted-foreground">/{site.slug}</p>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded ${
              site.status === 'published' 
                ? 'bg-green-500/20 text-green-500' 
                : 'bg-yellow-500/20 text-yellow-500'
            }`}>
              {site.status}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`/${site.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <Eye size={16} />
              Preview
            </a>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Save size={16} className="mr-2" />
              )}
              Save
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="editorial-container flex gap-1 pb-0">
          {[
            { id: 'sections', label: 'Sections', icon: Layout },
            { id: 'theme', label: 'Theme', icon: Palette },
            { id: 'settings', label: 'Settings', icon: Settings }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>
      </header>

      <div className="editorial-container py-6">
        <div className="max-w-3xl mx-auto">
          {/* Sections Tab */}
          {activeTab === 'sections' && (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground mb-2">
                💡 Drag sections by the grip handle to reorder them
              </p>
              {/* Sections List */}
              {site.sections?.map((section, idx) => (
                <SectionEditor
                  key={section.section_id}
                  section={section}
                  index={idx}
                  onUpdate={handleUpdateSection}
                  onDelete={handleDeleteSection}
                  artists={crArtists}
                  projects={crProjects}
                  companies={crCompanies}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  isDragging={draggedIndex === idx}
                />
              ))}

              {/* Add Section */}
              <Card className="p-4">
                <p className="text-sm font-medium mb-3">Add Section</p>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(SECTION_TYPES).map(([type, config]) => (
                    <button
                      key={type}
                      onClick={() => handleAddSection(type)}
                      className="p-3 text-left bg-muted/30 hover:bg-muted rounded transition-colors"
                    >
                      <config.icon size={16} className="mb-1 text-muted-foreground" />
                      <p className="text-sm font-medium">{config.label}</p>
                      <p className="text-xs text-muted-foreground">{config.description}</p>
                    </button>
                  ))}
                </div>
              </Card>
            </div>
          )}

          {/* Theme Tab */}
          {activeTab === 'theme' && (
            <div className="space-y-6">
              {/* Font Package */}
              <Card className="p-6">
                <h3 className="font-medium mb-4">Font Package</h3>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(FONT_PACKAGES).map(([key, pkg]) => (
                    <button
                      key={key}
                      onClick={() => setSite({ ...site, font_package: key })}
                      className={`p-4 text-left rounded border-2 transition-colors ${
                        site.font_package === key
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground'
                      }`}
                    >
                      <p className="font-medium">{pkg.name}</p>
                      <p className="text-sm text-muted-foreground">{pkg.preview}</p>
                    </button>
                  ))}
                </div>
              </Card>

              {/* Accent Color */}
              <Card className="p-6">
                <h3 className="font-medium mb-4">Accent Color</h3>
                <div className="flex gap-3 items-center">
                  <input
                    type="color"
                    value={site.accent_color || '#C41E3A'}
                    onChange={(e) => setSite({ ...site, accent_color: e.target.value })}
                    className="w-16 h-12 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={site.accent_color || '#C41E3A'}
                    onChange={(e) => setSite({ ...site, accent_color: e.target.value })}
                    className="flex-1 px-3 py-2 bg-background border border-border rounded"
                  />
                </div>
                <div className="flex gap-2 mt-3">
                  {['#C41E3A', '#FFD700', '#2563EB', '#059669', '#7C3AED', '#EC4899'].map(color => (
                    <button
                      key={color}
                      onClick={() => setSite({ ...site, accent_color: color })}
                      className="w-8 h-8 rounded-full border-2 border-white/20"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </Card>

              {/* Dark/Light Mode */}
              <Card className="p-6">
                <h3 className="font-medium mb-4">Color Mode</h3>
                <div className="flex gap-3">
                  <button
                    onClick={() => setSite({ ...site, dark_mode: true })}
                    className={`flex-1 p-4 rounded border-2 ${
                      site.dark_mode ? 'border-primary' : 'border-border'
                    }`}
                  >
                    <div className="w-full h-12 bg-gray-900 rounded mb-2" />
                    <p className="text-sm">Dark Mode</p>
                  </button>
                  <button
                    onClick={() => setSite({ ...site, dark_mode: false })}
                    className={`flex-1 p-4 rounded border-2 ${
                      !site.dark_mode ? 'border-primary' : 'border-border'
                    }`}
                  >
                    <div className="w-full h-12 bg-gray-100 rounded mb-2" />
                    <p className="text-sm">Light Mode</p>
                  </button>
                </div>
              </Card>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              {/* Basic Info */}
              <Card className="p-6 space-y-4">
                <h3 className="font-medium">Basic Information</h3>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Site Name</label>
                  <input
                    type="text"
                    value={site.name || ''}
                    onChange={(e) => setSite({ ...site, name: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Tagline</label>
                  <input
                    type="text"
                    value={site.tagline || ''}
                    onChange={(e) => setSite({ ...site, tagline: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded"
                    placeholder="Writer. Producer. Artist."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">SEO Description</label>
                  <textarea
                    value={site.description || ''}
                    onChange={(e) => setSite({ ...site, description: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded"
                    rows={3}
                    placeholder="Description for search engines..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">URL Slug</label>
                  <div className="flex items-center">
                    <span className="text-muted-foreground mr-1">/</span>
                    <input
                      type="text"
                      value={site.slug || ''}
                      onChange={(e) => setSite({ 
                        ...site, 
                        slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') 
                      })}
                      className="flex-1 px-3 py-2 bg-background border border-border rounded"
                    />
                  </div>
                </div>
              </Card>

              {/* Images */}
              <Card className="p-6 space-y-4">
                <h3 className="font-medium">Images</h3>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Logo URL</label>
                  <input
                    type="text"
                    value={site.logo || ''}
                    onChange={(e) => setSite({ ...site, logo: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded"
                    placeholder="https://..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">OG Image (for social sharing)</label>
                  <input
                    type="text"
                    value={site.og_image || ''}
                    onChange={(e) => setSite({ ...site, og_image: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded"
                    placeholder="https://..."
                  />
                </div>
              </Card>

              {/* Social Sharing URL */}
              <Card className="p-6 space-y-4 border-2 border-green-500/30">
                <h3 className="font-medium flex items-center gap-2">
                  <span className="text-green-500">⚠️</span> Social Sharing URL
                </h3>
                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded">
                  <p className="text-sm font-medium text-green-400 mb-2">📤 Use This URL When Sharing</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    <strong>Important:</strong> Copy this URL when sharing on Facebook, Twitter, LinkedIn, etc. 
                    This ensures your brand name, description, and image appear correctly instead of generic site info.
                  </p>
                  <div className="flex items-center gap-2 mb-3">
                    <code className="flex-1 px-3 py-2 bg-background border border-border rounded text-sm break-all text-green-400">
                      {`${API_URL}/api/og/brand/${site.slug || '[slug]'}`}
                    </code>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(`${API_URL}/api/og/brand/${site.slug}`);
                        alert('Share URL copied! Use this link when posting to social media.');
                      }}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium"
                    >
                      Copy
                    </button>
                  </div>
                  <div className="flex gap-2 text-xs">
                    <a 
                      href={`https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(`${API_URL}/api/og/brand/${site.slug}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:underline"
                    >
                      Test on Facebook →
                    </a>
                    <a 
                      href={`https://cards-dev.twitter.com/validator`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:underline"
                    >
                      Test on Twitter →
                    </a>
                    <a 
                      href={`https://www.linkedin.com/post-inspector/inspect/${encodeURIComponent(`${API_URL}/api/og/brand/${site.slug}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:underline"
                    >
                      Test on LinkedIn →
                    </a>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>Preview: <strong className="text-foreground">{site.name || 'Site Name'}</strong> - {site.tagline || 'Tagline'}</p>
                  <p className="text-yellow-500/80">Note: The direct URL (/{site.slug}) works for visitors but won't show correct preview when shared on social media.</p>
                </div>
              </Card>

              {/* Embed Code */}
              <Card className="p-6 space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <span className="text-blue-500">🔗</span> Embed Code
                </h3>
                <p className="text-xs text-muted-foreground">
                  Copy this code to embed your brand site on other websites. Adjust width and height as needed.
                </p>
                <div className="space-y-3">
                  {/* Standard Embed */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-2">Standard Embed (responsive)</label>
                    <div className="relative">
                      <textarea
                        readOnly
                        value={`<iframe src="${API_URL.replace('/api', '')}/${site.slug}" width="100%" height="600" frameborder="0" style="border:none; border-radius:8px;" allow="fullscreen" title="${site.name || 'Brand Site'}"></iframe>`}
                        className="w-full px-3 py-2 bg-background border border-border rounded text-xs font-mono h-20 resize-none"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`<iframe src="${API_URL.replace('/api', '')}/${site.slug}" width="100%" height="600" frameborder="0" style="border:none; border-radius:8px;" allow="fullscreen" title="${site.name || 'Brand Site'}"></iframe>`);
                          alert('Embed code copied!');
                        }}
                        className="absolute top-2 right-2 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                  
                  {/* Fixed Size Embed */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-2">Fixed Size (800x600)</label>
                    <div className="relative">
                      <textarea
                        readOnly
                        value={`<iframe src="${API_URL.replace('/api', '')}/${site.slug}" width="800" height="600" frameborder="0" style="border:none; border-radius:8px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);" allow="fullscreen" title="${site.name || 'Brand Site'}"></iframe>`}
                        className="w-full px-3 py-2 bg-background border border-border rounded text-xs font-mono h-20 resize-none"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`<iframe src="${API_URL.replace('/api', '')}/${site.slug}" width="800" height="600" frameborder="0" style="border:none; border-radius:8px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);" allow="fullscreen" title="${site.name || 'Brand Site'}"></iframe>`);
                          alert('Embed code copied!');
                        }}
                        className="absolute top-2 right-2 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs"
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  {/* Full Page Embed */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-2">Full Page (100% viewport)</label>
                    <div className="relative">
                      <textarea
                        readOnly
                        value={`<iframe src="${API_URL.replace('/api', '')}/${site.slug}" width="100%" height="100vh" frameborder="0" style="border:none; position:fixed; top:0; left:0; right:0; bottom:0; width:100%; height:100%;" allow="fullscreen" title="${site.name || 'Brand Site'}"></iframe>`}
                        className="w-full px-3 py-2 bg-background border border-border rounded text-xs font-mono h-20 resize-none"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(`<iframe src="${API_URL.replace('/api', '')}/${site.slug}" width="100%" height="100vh" frameborder="0" style="border:none; position:fixed; top:0; left:0; right:0; bottom:0; width:100%; height:100%;" allow="fullscreen" title="${site.name || 'Brand Site'}"></iframe>`);
                          alert('Embed code copied!');
                        }}
                        className="absolute top-2 right-2 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Direct Link: <a href={`${API_URL.replace('/api', '')}/${site.slug}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">{API_URL.replace('/api', '')}/{site.slug}</a>
                </p>
              </Card>

              {/* Social Links */}
              <Card className="p-6 space-y-4">
                <h3 className="font-medium">Social Links</h3>
                
                {['instagram', 'twitter', 'imdb', 'linkedin', 'youtube', 'website'].map(platform => (
                  <div key={platform}>
                    <label className="block text-sm font-medium mb-1 capitalize">{platform}</label>
                    <input
                      type="text"
                      value={site.social_links?.[platform] || ''}
                      onChange={(e) => setSite({ 
                        ...site, 
                        social_links: { ...site.social_links, [platform]: e.target.value }
                      })}
                      className="w-full px-3 py-2 bg-background border border-border rounded"
                      placeholder={`https://${platform}.com/...`}
                    />
                  </div>
                ))}
              </Card>

              {/* Status */}
              <Card className="p-6">
                <h3 className="font-medium mb-4">Status</h3>
                <div className="flex gap-3">
                  <button
                    onClick={() => setSite({ ...site, status: 'draft' })}
                    className={`flex-1 p-3 rounded border-2 ${
                      site.status === 'draft' ? 'border-yellow-500 bg-yellow-500/10' : 'border-border'
                    }`}
                  >
                    <p className="font-medium">Draft</p>
                    <p className="text-xs text-muted-foreground">Not visible to public</p>
                  </button>
                  <button
                    onClick={() => setSite({ ...site, status: 'published' })}
                    className={`flex-1 p-3 rounded border-2 ${
                      site.status === 'published' ? 'border-green-500 bg-green-500/10' : 'border-border'
                    }`}
                  >
                    <p className="font-medium">Published</p>
                    <p className="text-xs text-muted-foreground">Live and visible</p>
                  </button>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrandSiteEditorPage;
